import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useProperty } from '@/context/PropertyContext';
import { useToast } from '@/shared/hooks/use-toast';

function useProjectId(): string {
  const { selectedProperty } = useProperty();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  return selectedProperty?.id || urlProjectId || '';
}
import {
  getChecklists,
  getChecklistById,
  getChecklistWithDetails,
  createChecklistBulk,
  updateChecklistFullPartial,
  deleteChecklist,
  createChecklistFromTemplate,
  createInstanceForWorkOrder,
} from '../api/api';
import { ChecklistTemplate } from '../types';
import { Checklist, WorkOrder } from '@features/work-orders/types';

// Query key factories for better organization and invalidation
export const checklistKeys = {
  // Base key for all checklist queries
  all: ['checklists'] as const,

  // All lists for a specific project
  lists: (projectId: string) => [...checklistKeys.all, 'list', projectId] as const,

  // Templates only for a project
  templates: (projectId: string) => [...checklistKeys.lists(projectId), 'templates'] as const,

  // Instances only for a project
  instances: (projectId: string) => [...checklistKeys.lists(projectId), 'instances'] as const,

  // Individual detail queries
  details: () => [...checklistKeys.all, 'detail'] as const,
  detail: (id: string) => [...checklistKeys.details(), id] as const,

  // Full detail with sections/items
  detailFull: (id: string, projectId: string) =>
    [...checklistKeys.detail(id), 'full', projectId] as const,
} as const;

// Cache configuration
const CACHE_CONFIG = {
  CHECKLISTS: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: 'always' as const,
  },
  CHECKLIST_TEMPLATES: {
    staleTime: 5 * 60 * 1000, // 5 minutes (templates change less frequently)
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },
  INDIVIDUAL_CHECKLIST: {
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  },
} as const;

/**
 * Get all checklists for a project
 */
export const useChecklists = (isTemplate?: boolean, enabled: boolean = true) => {
  const projectId = useProjectId();

  const queryKey = projectId
    ? isTemplate === true
      ? checklistKeys.templates(projectId)
      : isTemplate === false
        ? checklistKeys.instances(projectId)
        : checklistKeys.lists(projectId)
    : undefined;

  return useQuery<ChecklistTemplate[]>({
    queryKey: queryKey || ['checklists-disabled'],
    enabled: enabled && !!projectId && !!queryKey,
    queryFn: () => {
      if (!projectId) {
        throw new Error('No project ID available');
      }
      return getChecklists(projectId, { isTemplate }).then((response) => response.results);
    },
    ...CACHE_CONFIG.CHECKLISTS,
  });
};

/**
 * Get checklist templates only
 * @param enabled - Whether the query should be enabled (default: true)
 */
export const useChecklistTemplates = (enabled: boolean = true) => {
  return useChecklists(true, enabled);
};

/**
 * Hook specifically for the templates dashboard - only fetches when explicitly enabled
 */
export const useTemplatesDashboard = () => {
  const projectId = useProjectId();
  return useChecklists(true, !!projectId);
};

/**
 * Get checklist instances only
 * @param enabled - Whether the query should be enabled (default: true)
 */
export const useChecklistInstances = (enabled: boolean = true) => {
  return useChecklists(false, enabled);
};

/**
 * Get a single checklist by ID (basic info)
 */
export const useChecklistById = (id: string) => {
  return useQuery<ChecklistTemplate>({
    queryKey: checklistKeys.detail(id),
    queryFn: () => getChecklistById(id),
    enabled: !!id,
    ...CACHE_CONFIG.INDIVIDUAL_CHECKLIST,
  });
};

/**
 * Get a single checklist with full details (sections, items, etc.)
 */
export const useChecklistWithDetails = (id: string, enabled: boolean = true) => {
  const projectId = useProjectId();

  return useQuery<ChecklistTemplate>({
    queryKey:
      id && projectId
        ? checklistKeys.detailFull(id, projectId)
        : ['checklist-detail-disabled'],
    queryFn: () => {
      if (!id) {
        throw new Error('No checklist ID provided');
      }
      if (!projectId) {
        throw new Error('No project ID available');
      }
      return getChecklistWithDetails(id, projectId);
    },
    enabled: enabled && !!id && !!projectId,
    ...CACHE_CONFIG.INDIVIDUAL_CHECKLIST,
  });
};

/**
 * Mutations for checklist operations with optimistic updates
 */
export const useChecklistMutations = () => {
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { toast } = useToast();

  const createTemplate = useMutation({
    mutationFn: (
      templateData: Omit<
        ChecklistTemplate,
        'id' | 'created_by' | 'created_at' | 'updated_by' | 'updated_at' | 'project'
      >
    ) => {
      if (!projectId) {
        throw new Error('No project selected');
      }
      return createChecklistBulk(projectId, templateData);
    },
    onMutate: async (newTemplate) => {
      if (!projectId) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: checklistKeys.lists(projectId) });

      // Snapshot the previous value
      const previousTemplates = queryClient.getQueryData<ChecklistTemplate[]>(
        checklistKeys.templates(projectId)
      );

      // Optimistically update the cache
      const optimisticTemplate: ChecklistTemplate = {
        id: `temp-${Date.now()}`,
        project: projectId,
        created_by: 'current-user',
        created_at: new Date().toISOString(),
        updated_by: 'current-user',
        updated_at: new Date().toISOString(),
        ...newTemplate,
      };

      queryClient.setQueryData<ChecklistTemplate[]>(
        checklistKeys.templates(projectId),
        (old) => [...(old || []), optimisticTemplate]
      );

      return { previousTemplates, optimisticTemplate };
    },
    onError: (_err, _newTemplate, context) => {
      if (projectId && context?.previousTemplates) {
        queryClient.setQueryData(
          checklistKeys.templates(projectId),
          context.previousTemplates
        );
      }
      toast({
        title: 'Error',
        description: 'Failed to create template',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
        duration: 3000,
      });
    },
    onSuccess: (newTemplate, _data, context) => {
      if (projectId) {
        // Replace optimistic template with real data
        if (context?.optimisticTemplate) {
          queryClient.setQueriesData(
            { queryKey: checklistKeys.lists(projectId) },
            (oldData: ChecklistTemplate[] | undefined) => {
              if (!oldData) return oldData;
              return oldData.map((template) =>
                template.id === context.optimisticTemplate.id ? newTemplate : template
              );
            }
          );
        }

        // Set the new template in detail cache
        queryClient.setQueryData(checklistKeys.detail(newTemplate.id), newTemplate);
        queryClient.setQueryData(
          checklistKeys.detailFull(newTemplate.id, projectId),
          newTemplate
        );

        // Invalidate lists to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: checklistKeys.lists(projectId),
        });
      }

      toast({
        title: 'Success',
        description: 'Template created successfully',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
    },
  });

  const updateTemplate = useMutation({
    mutationFn: (data: { id: string; updates: Partial<ChecklistTemplate> }) => {
      if (!projectId) {
        throw new Error('No project selected');
      }
      return updateChecklistFullPartial(data.id, data.updates, projectId);
    },
    onMutate: async ({ id, updates }) => {
      if (!projectId) return;

      // Cancel queries for this checklist
      await queryClient.cancelQueries({ queryKey: checklistKeys.detail(id) });
      await queryClient.cancelQueries({
        queryKey: checklistKeys.detailFull(id, projectId),
      });

      // Snapshot previous values
      const previousDetail = queryClient.getQueryData<ChecklistTemplate>(checklistKeys.detail(id));
      const previousDetailFull = queryClient.getQueryData<ChecklistTemplate>(
        checklistKeys.detailFull(id, projectId)
      );

      // Optimistically update caches
      const updateFn = (old: ChecklistTemplate | undefined) => {
        if (!old) return old;
        return { ...old, ...updates, updated_at: new Date().toISOString() };
      };

      queryClient.setQueryData(checklistKeys.detail(id), updateFn);
      queryClient.setQueryData(checklistKeys.detailFull(id, projectId), updateFn);

      // Update in lists as well
      queryClient.setQueriesData(
        { queryKey: checklistKeys.lists(projectId) },
        (oldData: ChecklistTemplate[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((template) =>
            template.id === id
              ? { ...template, ...updates, updated_at: new Date().toISOString() }
              : template
          );
        }
      );

      return { previousDetail, previousDetailFull };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousDetail) {
        queryClient.setQueryData(checklistKeys.detail(id), context.previousDetail);
      }
      if (context?.previousDetailFull && projectId) {
        queryClient.setQueryData(
          checklistKeys.detailFull(id, projectId),
          context.previousDetailFull
        );
      }
      toast({
        title: 'Error',
        description: 'Failed to update template',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
        duration: 3000,
      });
    },
    onSuccess: (updatedTemplate, { id }) => {
      if (projectId) {
        // Update with actual server response
        queryClient.setQueryData(checklistKeys.detail(id), updatedTemplate);
        queryClient.setQueryData(
          checklistKeys.detailFull(id, projectId),
          updatedTemplate
        );

        // Invalidate lists to ensure consistency
        queryClient.invalidateQueries({
          queryKey: checklistKeys.lists(projectId),
        });
      }

      toast({
        title: 'Success',
        description: 'Template updated successfully',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: string) => {
      if (!projectId) {
        throw new Error('No project selected');
      }
      return deleteChecklist(id, projectId);
    },
    onMutate: async (deletedId) => {
      if (!projectId) return;

      // Cancel queries
      await queryClient.cancelQueries({ queryKey: checklistKeys.detail(deletedId) });
      await queryClient.cancelQueries({ queryKey: checklistKeys.lists(projectId) });

      // Snapshot previous values
      const previousTemplate = queryClient.getQueryData<ChecklistTemplate>(
        checklistKeys.detail(deletedId)
      );
      const previousLists = queryClient.getQueriesData({
        queryKey: checklistKeys.lists(projectId),
      });

      // Optimistically remove from caches
      queryClient.removeQueries({ queryKey: checklistKeys.detail(deletedId) });
      queryClient.removeQueries({
        queryKey: checklistKeys.detailFull(deletedId, projectId),
      });

      queryClient.setQueriesData(
        { queryKey: checklistKeys.lists(projectId) },
        (oldData: ChecklistTemplate[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((template) => template.id !== deletedId);
        }
      );

      return { previousTemplate, previousLists };
    },
    onError: (_err, deletedId, context) => {
      // Rollback on error
      if (context?.previousTemplate) {
        queryClient.setQueryData(checklistKeys.detail(deletedId), context.previousTemplate);
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
        duration: 3000,
      });
    },
    onSuccess: (_, deletedId) => {
      if (projectId) {
        // Ensure removal from cache
        queryClient.removeQueries({ queryKey: checklistKeys.detail(deletedId) });
        queryClient.removeQueries({
          queryKey: checklistKeys.detailFull(deletedId, projectId),
        });

        // Invalidate lists
        queryClient.invalidateQueries({
          queryKey: checklistKeys.lists(projectId),
        });
      }

      toast({
        title: 'Success',
        description: 'Template deleted successfully',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
    },
  });

  const createInstance = useMutation({
    mutationFn: (data: { templateId: string; title: string; area?: number | null }) => {
      if (!projectId) {
        throw new Error('No project selected');
      }
      return createChecklistFromTemplate(
        data.templateId,
        { title: data.title, area: data.area ?? null },
        projectId
      );
    },
    onSuccess: (newInstance) => {
      if (projectId) {
        // Add to instances cache
        queryClient.setQueryData<ChecklistTemplate[]>(
          checklistKeys.instances(projectId),
          (old) => [...(old || []), newInstance]
        );

        // Set in detail cache
        queryClient.setQueryData(checklistKeys.detail(newInstance.id), newInstance);
        queryClient.setQueryData(
          checklistKeys.detailFull(newInstance.id, projectId),
          newInstance
        );

        // Invalidate lists
        queryClient.invalidateQueries({
          queryKey: checklistKeys.lists(projectId),
        });
      }

      toast({
        title: 'Success',
        description: 'Punch list created from template',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create punch list from template',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
        duration: 3000,
      });
    },
  });

  const createInstanceForWorkOrderMutation = useMutation({
    mutationFn: async (data: {
      templateId: string;
      title: string;
      workOrderId: string;
    }): Promise<{ checklist: Checklist; workOrder: WorkOrder }> => {
      if (!projectId) {
        throw new Error('No project selected');
      }

      // Use the simplified function
      return createInstanceForWorkOrder(
        data.templateId,
        data.title,
        data.workOrderId,
        projectId
      );
    },
    onSuccess: (result: { checklist: Checklist; workOrder: WorkOrder }) => {
      const { checklist } = result;
      if (projectId) {
        // Add to instances cache
        queryClient.setQueryData<ChecklistTemplate[]>(
          checklistKeys.instances(projectId),
          (old) => [...(old || []), checklist]
        );

        // Set in detail cache
        queryClient.setQueryData(checklistKeys.detail(checklist.id), checklist);
        queryClient.setQueryData(
          checklistKeys.detailFull(checklist.id, projectId),
          checklist
        );

        // Invalidate checklist lists
        queryClient.invalidateQueries({
          queryKey: checklistKeys.lists(projectId),
        });

        // Invalidate work orders cache to reflect the updated work order
        queryClient.invalidateQueries({
          queryKey: ['work-orders'],
        });
      }

      toast({
        title: 'Success',
        description: 'Checklist created and attached to work order',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create checklist from template',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
        duration: 3000,
      });
    },
  });

  return {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    createInstance,
    createInstanceForWorkOrderMutation,
  };
};

/**
 * Utility hook for navigation
 */
export const useChecklistNavigation = () => {
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  const getBasePath = () => `/app/property/${projectId}/punch-lists`;

  const navigateToTemplate = (templateId: string, navigate: (path: string) => void) => {
    navigate(`${getBasePath()}/templates/${templateId}`);
  };

  const navigateToBuilder = (navigate: (path: string) => void, templateId?: string) => {
    if (templateId) {
      navigate(`${getBasePath()}/templates/${templateId}`);
    } else {
      navigate(`${getBasePath()}/templates/new`);
    }
  };

  const navigateToDashboard = (navigate: (path: string) => void, templateId?: string) => {
    if (templateId) {
      queryClient.invalidateQueries({
        queryKey: checklistKeys.detail(templateId),
      });
    }

    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: checklistKeys.lists(projectId),
        refetchType: 'none',
      });
    }

    navigate(getBasePath());
  };

  const prefetchTemplate = (templateId: string) => {
    if (projectId) {
      queryClient.prefetchQuery({
        queryKey: checklistKeys.detailFull(templateId, projectId),
        queryFn: () => getChecklistWithDetails(templateId, projectId),
        staleTime: 60 * 1000,
      });
    }
  };

  return {
    navigateToTemplate,
    navigateToBuilder,
    navigateToDashboard,
    prefetchTemplate,
  };
};
